import { ModalDialog } from '../../lib/ModalDialog'

/**
 * First-run welcome modal that introduces the three Meshinator workflows.
 *
 * Shown once per browser; dismissal is recorded in `localStorage` under
 * `meshinator-welcome-seen`. The same content is reused by the "Learn" link
 * (via `Generators.get_learning_resources_html`) so users can re-open it.
 */
const STORAGE_KEY = 'meshinator-welcome-seen'

/**
 * The welcome / quick-start HTML body. Exported so both this modal and the
 * navbar's "Learn" link render the same content. CSS lives in styles.css under
 * `.welcome-workflow-*` rules.
 */
export const WELCOME_HTML = `
  <p style="margin-top: 0;">Meshinator is a browser-based 3D animation studio. Three ways to get started:</p>
  <div class="welcome-workflow-grid">
    <a class="welcome-workflow-card" href="/retarget/index.html">
      <div class="welcome-workflow-icon">🤖</div>
      <div class="welcome-workflow-title">I have a rigged model</div>
      <div class="welcome-workflow-desc">From Tripo3D, Mixamo, Quaternius, or your own rigging pipeline. Drop the GLB / FBX and we auto-detect the skeleton.</div>
    </a>
    <a class="welcome-workflow-card" href="/create.html">
      <div class="welcome-workflow-icon">🧱</div>
      <div class="welcome-workflow-title">I have an unrigged mesh</div>
      <div class="welcome-workflow-desc">Drop a raw GLB / FBX. Pick a skeleton template and fit it to your mesh.</div>
    </a>
    <div class="welcome-workflow-card welcome-workflow-card--passive">
      <div class="welcome-workflow-icon">👋</div>
      <div class="welcome-workflow-title">Just exploring</div>
      <div class="welcome-workflow-desc">Pick a sample character from the left side and play with animations, pose-and-bake authoring, and webcam mocap.</div>
    </div>
  </div>
  <h3 style="margin-top: 1.5rem;">Inside the editor</h3>
  <ul>
    <li><strong>Animation library</strong> — pick a clip from the right-hand list and watch your rig perform it.</li>
    <li><strong>📹 Mocap</strong> — drive the rig live from your webcam (first activation downloads ~10MB of MediaPipe models).</li>
    <li><strong>⏺️ Record</strong> — capture a mocap performance into a user-authored clip.</li>
    <li><strong>+ New Clip</strong> &amp; <strong>Pose Mode</strong> — author a clip by hand: drag bones with the gizmo, click <strong>Bake Pose</strong> to capture keyframes.</li>
    <li><strong>Import animations</strong> — drop a GLB or FBX. Mixamo, Quaternius, and Tripo3D rigs auto-retarget on import.</li>
  </ul>
`

export class WelcomeModal {
  /** Show the modal regardless of whether it's been seen before. */
  static show (): void {
    new ModalDialog('Welcome to Meshinator', WELCOME_HTML, { customClass: 'welcome-dialog' }).show()
    WelcomeModal.mark_seen()
  }

  /** Show the modal only if the user hasn't seen it yet. Safe to call on every page load. */
  static show_if_first_run (): void {
    if (WelcomeModal.has_seen()) return
    WelcomeModal.show()
  }

  private static has_seen (): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch (_) { return false }
  }

  private static mark_seen (): void {
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch (_) { /* private mode, quota exceeded — non-fatal */ }
  }
}
